import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { Search, ShoppingCart, Bell, Mic, QrCode, MapPin, ChevronDown, Zap, Store, LayoutGrid, Home as HomeIcon, Menu, Sparkles, LogOut, User, Package, Calculator, ScanBarcode, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import CartDrawer from "@/components/CartDrawer";
import LocationPicker from "@/components/LocationPicker";
import AdminUnlock from "@/components/AdminUnlock";
import { api } from "@/lib/api";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

export default function StoreLayout() {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const [deliverTo, setDeliverTo] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  // Hidden owner entry: 10 quick taps on the Rani Super Market logo (within 6s), then AdminUnlock opens silently ~10s LATER — kisi ko gesture ka pata nahi chalta. Single tap still goes home; StoreLayout stays mounted so taps accumulate across pages. Counter resets if paused.
  const tapCountRef = useRef(0);
  const tapTimerRef = useRef(null);
  const openTimerRef = useRef(null);
  const [tapCount, setTapCount] = useState(0);
  const onLogoTap = () => {
    nav("/");
    tapCountRef.current += 1;
    setTapCount(tapCountRef.current);
    clearTimeout(tapTimerRef.current);
    if (tapCountRef.current >= 10) {
      tapCountRef.current = 0;
      setTapCount(0);
      clearTimeout(openTimerRef.current);
      openTimerRef.current = setTimeout(() => setUnlockOpen(true), 10000);
      return;
    }
    tapTimerRef.current = setTimeout(() => { tapCountRef.current = 0; setTapCount(0); }, 6000);
  };

  useEffect(() => {
    if (!user) { setUnreadCount(0); return; }
    let alive = true;
    const fetchUnread = () => api.get("/notifications")
      .then((r) => { if (alive) setUnreadCount((r.data || []).filter((n) => !n.read).length); })
      .catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 45000);
    return () => { alive = false; clearInterval(t); };
  }, [user, loc.pathname]);

  useEffect(() => {
    if (user) {
      api.get("/addresses")
        .then((r) => { const list = r.data || []; setDeliverTo(list.find((a) => a.is_default) || list[0] || null); })
        .catch(() => {});
    } else {
      try { setDeliverTo(JSON.parse(localStorage.getItem("rsmart_delivery_loc") || "null")); } catch { setDeliverTo(null); }
    }
  }, [user]);

  const saveDeliveryLocation = async (loc) => {
    if (user) {
      try {
        await api.post("/addresses", {
          label: loc.label || "Home", name: user.name || "Customer", phone: user.phone || "",
          line1: loc.line1 || loc.display, line2: "", city: loc.city || "Ranchi",
          state: loc.state || "Jharkhand", pincode: loc.pincode || "834001",
          is_default: true, lat: loc.lat, lng: loc.lng,
        });
        toast.success("Delivery address update ho gaya!");
      } catch { toast.error("Address save nahi ho paya"); return; }
    } else {
      localStorage.setItem("rsmart_delivery_loc", JSON.stringify(loc));
      toast.success("Delivery location set ho gayi — order ke liye login karein");
    }
    setDeliverTo(loc);
    setPickerOpen(false);
  };

  useEffect(() => { setQ(new URLSearchParams(loc.search).get("q") || ""); }, [loc.search]);
  useEffect(() => {
    const ref = (new URLSearchParams(loc.search).get("ref") || "").trim();
    if (ref) {
      localStorage.setItem("rsmart_referral", ref.toUpperCase());
      toast.success(`Referral code ${ref.toUpperCase()} laga diya — checkout par apply hoga`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const submit = (e) => { e.preventDefault(); nav(`/search?q=${encodeURIComponent(q)}`); };

  const bottomNav = [
    { icon: HomeIcon, label: "Home", to: "/" },
    { icon: LayoutGrid, label: "Shop", to: "/search" },
    { icon: Menu, label: "Menu", to: "/search" },
    { icon: Store, label: "Local Store", to: "/" },
    { icon: ShoppingCart, label: "Cart", action: () => setOpen(true), badge: count },
    { icon: Sparkles, label: "Ask AI", action: () => toast("Ask AI coming soon!") },
  ];

  return (
    <div className="min-h-screen bg-[#f1f3f6] flex flex-col overflow-x-hidden">
      {/* Top utility strip */}
      <aside data-testid="top-utility-strip" className="bg-amber-50 border-b border-amber-200 text-xs text-amber-900 px-4 py-1.5">
        <div className="max-w-7xl mx-auto w-full flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
            <span className="font-medium truncate">Welcome to Rani Super Market & Rs. MART POS Official Store. Best deals on Electronics, Appliances & POS Setup — fast local delivery!</span>
          </div>
          <div className="flex items-center gap-4 text-xs font-semibold shrink-0">
            <Link to="/download-app" data-testid="topbar-app-link" className="text-emerald-700 hover:underline flex items-center gap-1"><Smartphone size={13} /> <span className="hidden xs:inline">Get the</span> App</Link>
            <span className="hidden sm:inline text-amber-300">|</span>
            <Link to="/page/contact" data-testid="find-store-link" className="hidden sm:flex hover:underline items-center gap-1"><Store size={13} /> Find Nearby Supermarket</Link>
            <span className="hidden sm:inline text-amber-300">|</span>
            <Link to="/page/contact" data-testid="pos-demo-link" className="hidden sm:flex text-indigo-700 hover:underline items-center gap-1"><Calculator size={13} /> Request Free POS Demo</Link>
          </div>
        </div>
      </aside>

      {/* Main header (white, design same-to-same) */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-2 sm:px-3 lg:px-6 py-3 flex flex-col gap-3">
          {/* Row 1: brand cards + actions */}
          <div className="flex items-center justify-between gap-2 sm:gap-3 w-full">
            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 max-w-[52%] sm:max-w-[55%]">
              <div role="button" tabIndex={0} data-testid="logo-home"
                onClick={onLogoTap}
                onContextMenu={(e) => e.preventDefault()}
                style={{ touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none", WebkitTouchCallout: "none" }}
                className="relative h-11 sm:h-16 max-w-[92px] sm:max-w-none flex items-center justify-center px-1.5 sm:px-2 rounded-xl bg-white border border-red-600/10 hover:border-red-600/30 transition-all shadow-sm hover:scale-[1.01] shrink-0 overflow-hidden cursor-pointer" title="Rani Super Market - Quality Guarantee Ke Saath">
                <img src="/rani-logo.png" alt="Rani Super Market Logo" className="h-full w-auto max-w-full object-contain rounded-md" draggable={false} />
                {tapCount >= 3 && <div data-testid="tap-progress" className="absolute bottom-0 left-0 h-1 bg-red-600 transition-all duration-150" style={{ width: `${Math.min(tapCount, 10) * 10}%` }} />}
              </div>
              <div data-testid="pos-brand-badge"
                onClick={() => nav("/rs-mart-pos")}
                className="h-11 sm:h-16 flex items-center gap-2 sm:gap-2.5 px-2 sm:px-3 rounded-xl bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-all shadow-sm hover:scale-[1.01] cursor-pointer min-w-0 shrink-0" title="Rs. MART POS — Premium Billing & Inventory ERP (Subscription)">
                <img src="/rsmart-pos-logo.png" alt="Rs. MART POS Badge" draggable={false} className="h-8 w-8 sm:h-10 sm:w-10 rounded-full object-cover border-2 border-blue-400 bg-white shrink-0" />
                {tapCount >= 3 && <div data-testid="tap-progress" className="absolute bottom-0 left-0 h-1 bg-blue-600 transition-all duration-150" style={{ width: `${Math.min(tapCount, 10) * 10}%` }} />}
                <div className="text-left leading-tight min-w-0 hidden sm:block">
                  <span className="text-xs font-extrabold uppercase tracking-wider text-blue-900 flex items-center gap-1.5">Rs. MART <span className="bg-blue-600 text-white text-[10px] px-1.5 py-0.5 rounded font-black">POS</span></span>
                  <span className="text-[11px] text-blue-700 font-semibold block mt-0.5 truncate">Smart Billing Solutions</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2.5 lg:gap-4 shrink-0 text-sm font-medium text-slate-700">
              <button data-testid="nav-notifications" onClick={() => nav(user ? "/notifications" : "/login")} aria-label="Notifications" className="relative p-1.5 sm:p-2 rounded-full hover:bg-slate-100 text-slate-700 hover:text-blue-600 transition-colors border border-slate-200/80 bg-slate-50">
                <Bell size={19} />
                {user && unreadCount > 0 && <span data-testid="notif-unread-count" className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-red-600 text-white text-[10px] font-bold rounded-full border-2 border-white flex items-center justify-center leading-none">{unreadCount > 9 ? "9+" : unreadCount}</span>}
              </button>
              <button data-testid="nav-local-sellers" onClick={() => nav("/local-sellers")} className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200/80 text-amber-800 hover:bg-amber-100 transition-colors shadow-sm font-semibold text-xs" title="Local Delivery Sellers">
                <Store size={15} className="text-amber-600" /><span className="hidden sm:inline">Local Shops</span>
              </button>
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="account-menu" className="flex items-center gap-2 px-2.5 sm:px-3.5 py-1.5 rounded-md hover:bg-slate-100 font-semibold text-slate-800 transition-colors">
                      <User size={16} className="text-blue-600" />
                      <span className="max-w-[90px] truncate hidden sm:inline">{user.name?.split(" ")[0]}</span>
                      <ChevronDown size={14} className="text-slate-400" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-white text-slate-800">
                    <DropdownMenuItem data-testid="menu-orders" onClick={() => nav("/orders")}><Package size={15} className="mr-2" /> My Orders</DropdownMenuItem>
                    <DropdownMenuItem data-testid="menu-profile" onClick={() => nav("/profile")}><User size={15} className="mr-2" /> Profile & Addresses</DropdownMenuItem>
                    {user.role === "seller" &&
                      <DropdownMenuItem data-testid="menu-seller" onClick={() => nav("/seller")}><Store size={15} className="mr-2" /> Seller Dashboard</DropdownMenuItem>}
                    <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav("/"); }}><LogOut size={15} className="mr-2" /> Logout</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <button data-testid="nav-login" onClick={() => nav("/login")} className="flex items-center gap-2 px-2.5 sm:px-3.5 py-1.5 rounded-md hover:bg-slate-100 font-semibold text-slate-800 transition-colors">
                  <User size={16} className="text-blue-600" /><span className="hidden sm:inline">Login</span><ChevronDown size={14} className="text-slate-400 hidden sm:block" />
                </button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="more-menu" className="hidden md:flex items-center gap-1 hover:text-blue-600 transition-colors"><span>More</span><ChevronDown size={14} className="text-slate-400" /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44 bg-white text-slate-800">
                  <DropdownMenuItem onClick={() => nav("/page/about")}>About Us</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/page/contact")}>Contact</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/page/terms")}>Terms</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/page/privacy")}>Privacy</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Row 2: location card + search + cart */}
          <div className="w-full flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
            <button data-testid="location-card" onClick={() => setPickerOpen(true)} className="flex items-center justify-between gap-3 text-slate-700 bg-slate-50 border border-slate-200 hover:border-blue-400 rounded-lg px-3 py-1.5 shrink-0 shadow-sm transition-colors sm:max-w-sm sm:w-[340px] text-left">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center shrink-0 border border-red-600/10"><MapPin size={15} className="text-red-600" /></div>
                <div className="leading-tight min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-slate-400 font-medium leading-none">Deliver to</span>
                    <span className="inline-flex items-center gap-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-bold px-1.5 py-0.5 rounded border border-emerald-200/80 leading-none shrink-0"><Zap size={9} className="fill-emerald-500" /> 5 day</span>
                  </div>
                  <div className="font-bold text-slate-800 text-xs truncate mt-0.5" data-testid="location-card-address">
                    {deliverTo ? `${deliverTo.label || "Home"} — ${deliverTo.line1}` : "Vill, Ginjo Thakurgaon, Jharkhand"}
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-500 mt-0.5">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                    <span className="font-mono font-medium truncate">
                      {deliverTo ? `${[deliverTo.city, deliverTo.pincode].filter(Boolean).join(" - ")}` : "GPS: 23.5112° N, 85.1723° E"}
                    </span>
                  </div>
                </div>
              </div>
              <ChevronDown size={15} className="text-slate-400 shrink-0 ml-1" />
            </button>
            <form onSubmit={submit} className="relative flex-1 flex items-center">
              <div className="relative w-full">
                <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search for LED TVs, Washing Machines, Fans, Mixers, Inverters & more..." className="w-full pl-11 pr-24 py-2.5 bg-sky-50/50 border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white placeholder-slate-400 shadow-inner" />
                <button type="submit" aria-label="Search" className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600"><Search size={16} /></button>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2 text-slate-400">
                  <button type="button" title="Voice Search" onClick={() => toast("Voice search coming soon!")} className="hover:text-blue-600"><Mic size={16} /></button>
                  <button type="button" title="Scan Barcode / Search with Image" onClick={() => toast("Barcode scan coming soon!")} className="hover:text-blue-600"><ScanBarcode size={16} /></button>
                </div>
              </div>
            </form>
            <button data-testid="header-cart-btn" onClick={() => setOpen(true)} className="flex items-center justify-center gap-2 text-slate-800 hover:text-blue-600 font-semibold transition-colors relative shrink-0 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg shadow-sm">
              <span className="relative">
                <ShoppingCart size={19} className="text-slate-700" />
                {count > 0 && <span className="absolute -top-2 -right-2.5 bg-red-600 text-white text-[10px] font-black rounded-full h-4 w-4 flex items-center justify-center">{count}</span>}
              </span>
              <span className="text-sm font-bold text-slate-800">Cart</span>
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 pb-20 lg:pb-0"><Outlet /></main>

      {/* Bottom tab bar (mobile) */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200 flex items-center justify-around px-1 py-1.5">
        {bottomNav.map((it) => {
          const active = it.to && (it.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(it.to));
          return (
            <button key={it.label} data-testid={`bottom-nav-${it.label.toLowerCase().replace(/ /g, "-")}`}
              onClick={() => (it.action ? it.action() : nav(it.to))}
              className={`relative flex flex-col items-center gap-0.5 px-1.5 py-1 rounded-lg min-w-0 ${active ? "text-blue-700" : "text-slate-500"}`}>
              <it.icon size={20} className={active ? "fill-blue-100" : ""} />
              <span className="text-[10px] font-semibold whitespace-nowrap">{it.label}</span>
              {it.badge > 0 && <span className="absolute top-0 right-0 bg-red-600 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-bold">{it.badge}</span>}
              {active && <span className="absolute -bottom-1.5 w-6 h-0.5 bg-blue-700 rounded-full" />}
            </button>
          );
        })}
      </nav>

      {/* Site footer (design same-to-same, 5 columns) */}
      <footer className="hidden lg:block bg-slate-900 text-slate-300 text-xs mt-10 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-12 grid grid-cols-2 md:grid-cols-5 gap-8">
          <div className="space-y-3">
            <img src="/rani-logo.png" alt="Rani Super Market Logo" className="h-9 w-auto object-contain rounded" />
            <p className="text-[11px] text-slate-400 leading-relaxed">Your neighborhood trust for pristine daily essentials, electronics, and kitchen provisions with unmatched savings.</p>
            <div className="text-[11px] text-slate-400"><span className="font-bold text-white block">Helpline:</span> +91 99559 94441</div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 mb-2">
              <img src="/rsmart-pos-logo.png" alt="Rs. MART POS Logo" className="h-7 w-7 rounded-full bg-white" />
              <h4 className="font-bold text-white text-xs uppercase tracking-wider">Rs. MART POS</h4>
            </div>
            <ul className="space-y-1.5 text-slate-400">
              {["Touch Billing Terminals", "Thermal Receipt Printers", "1D & 2D Barcode Scanners", "Electronic Weighing Scales", "GST Supermarket Software", "Book a Technician"].map((t) => (
                <li key={t}><Link to="/page/contact" className="hover:text-white">{t}</Link></li>
              ))}
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="font-bold text-white text-xs uppercase tracking-wider mb-2">Help & Support</h4>
            <ul className="space-y-1.5 text-slate-400">
              <li><Link to="/orders" className="hover:text-white">Track Delivery Status</Link></li>
              <li><Link to="/page/returns" className="hover:text-white">Return & Refund Policy</Link></li>
              <li><Link to="/page/about" className="hover:text-white">Quality Guarantee Claim</Link></li>
              <li><Link to="/page/contact" className="hover:text-white">Bulk B2B Wholesale Order</Link></li>
              <li><Link to="/orders" className="hover:text-white">Download Invoices</Link></li>
              <li><Link to="/download-app" className="hover:text-white">Download Android App</Link></li>
              <li><Link to="/downloads" className="hover:text-white">Downloads &amp; Media</Link></li>
              <li><Link to="/page/contact" className="hover:text-white">Contact Store Manager</Link></li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="font-bold text-white text-xs uppercase tracking-wider mb-2">Popular Aisles</h4>
            <ul className="space-y-1.5 text-slate-400">
              {[["Smart LED TVs", "tv"], ["Washing Machines", "washing machine"], ["Mixer Grinders", "mixer"], ["Inverters & Batteries", "inverter"], ["Fans & Coolers", "fan"]].map(([t, qy]) => (
                <li key={t}><Link to={`/search?q=${qy}`} className="hover:text-white">{t}</Link></li>
              ))}
            </ul>
          </div>
          <div className="col-span-2 md:col-span-1 space-y-2">
            <h4 className="font-bold text-white text-xs uppercase tracking-wider mb-2">Corporate Office</h4>
            <p className="text-[11px] text-slate-400 leading-relaxed">Rani Super Market & Rs. MART POS HQ<br />Vill, Ginjo Thakurgaon,<br />Jharkhand, India</p>
            <div className="pt-2">
              <span className="text-[10px] font-bold text-slate-300 block mb-1">We Accept:</span>
              <div className="flex items-center gap-2 text-slate-400 flex-wrap">
                {["UPI", "Visa / Rupay", "NetBanking", "Cash on Delivery"].map((t) => (
                  <span key={t} className="px-2 py-0.5 bg-slate-800 rounded border border-slate-700 text-[10px]">{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="border-t border-slate-800/80 bg-slate-950 py-4">
          <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-slate-500">
            <div className="flex items-center gap-3">
              <span>© 2026 Rani Super Market & Rs. MART POS. All rights reserved.</span>
              <span>•</span>
              <span className="text-slate-400">Quality, Guarantee Ke Saath.</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/page/privacy" className="hover:text-slate-300">Privacy Policy</Link>
              <Link to="/page/terms" className="hover:text-slate-300">Terms of Use</Link>
              <Link to="/page/returns" className="hover:text-slate-300">POS Hardware Warranty Terms</Link>
            </div>
          </div>
        </div>
      </footer>
      <CartDrawer />
      <AdminUnlock open={unlockOpen} onClose={() => setUnlockOpen(false)} />
      <LocationPicker open={pickerOpen} onClose={() => setPickerOpen(false)} initial={deliverTo} onSave={saveDeliveryLocation} />
    </div>
  );
}
